import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { endpoints } from "../../../../../core/defaultValues";
import { clientQuery } from "../../../../../core/data/GraphQLApi";
import { resetALL } from "../../../../../.globals/redux/actions";
import { ExceptionManager } from "../../../../../core/logManager";
import { clientMutation } from "Core/data/GraphQLApi";
// Helpers
import { validateJsonObject } from 'Core/data/Helpers';

const INIT_STATE = {
    loading: false,
    events: [],
    total: 0,
    error: null,
    nextToken: null,
    bulkResponse: null,
    loadBulkCreateEventCases: false,
};

const route = "modules/driverPerformance/views/events/redux/slice";

const driverPerformanceSearchEvents = createAsyncThunk(
    "driverPerformance/eventsTab/searchVideoReadings",
    async (payload, { rejectWithValue, getState }) => {
        const { conditions } = payload;

        let dateRange = conditions.find((item) => item.range);
        let pagination = conditions.find((item) => item.page);
        let tags = conditions.find((item) => item.tagIds);
        let events = conditions.find((item) => item.events);
        let drivers = conditions.find((item) => item.drivers);
        let status = conditions.find((item) => item.status);
        let openSearch = conditions.find((item) => item.openSearch);
        let driverDisputed = conditions.find((item) => item.driverDisputed);
        let excludedNoIssueEvents = false
        let excludedNoIssue = conditions.find((item) => item.excludedNoIssueEvents);
        if(excludedNoIssue){
            excludedNoIssueEvents = true
        }

        const state = getState();
        const currentNextToken = pagination?.page == 1 ? "" : state.driverPerformanceEventsTabRedux.nextToken;

        try {
            const response = await clientQuery(
                driverPerformanceSearchEvents.query,
                {
                    openSearch: openSearch?.openSearch ? openSearch.openSearch.trim() : "",
                    dateRange: {
                        start: dateRange?.range.GTE,
                        end: dateRange?.range.LTE
                    },
                    driverIds: drivers?.drivers?.length ? drivers?.drivers : [],
                    status: status?.status?.length ? status?.status : [],
                    eventNames: events?.events?.length ? events?.events : [],
                    tagIds: tags?.tagIds?.length ? tags?.tagIds : [],
                    driverDisputed: driverDisputed?.driverDisputed || false,
                    excludedNoIssueEvents: excludedNoIssueEvents,
                    limit: 50,
                    nextToken: currentNextToken || ""
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data ? response?.data : { items: [], nextToken: null };
        } catch (exc) {
            ExceptionManager(exc, route, "searchVideoReadings");
            return rejectWithValue(exc);
        }
    }
);

export const driverPerformanceSearchEntities = async (input = '') => {
    try {
        let QUERY = `
            query searchDriverPerformanceEntities ($openSearch: String!) {
                data: searchDriverPerformanceEntities (openSearch: $openSearch) {
                    id
                    label
                    type
                }
            }
        `
        return await clientQuery(QUERY, { openSearch: input }, endpoints.GRAPHQL_GENERAL_V2);
    } catch (exc) {
        ExceptionManager(exc, 'Modules/activities/.components/tab/redux/saga', 'getAccounts');
    }
}

driverPerformanceSearchEvents.query = `
    query searchDriverPerformanceEvents ($openSearch: String, $dateRange: AWSDateTimeRange, $limit: Int!, $driverIds: [Int], $eventNames: [String], $tagIds: [String], $nextToken: String, $status: [String], $driverDisputed: Boolean, $excludedNoIssueEvents: Boolean) {
    data: searchDriverPerformanceEvents (openSearch: $openSearch, dateRange: $dateRange, limit: $limit, driverIds: $driverIds, eventNames: $eventNames, tagIds: $tagIds, nextToken: $nextToken, status: $status, driverDisputed: $driverDisputed, excludedNoIssueEvents: $excludedNoIssueEvents) {
        items {
            rowId
            deviceId
            eventName
            eventCode
            driverId
            driverName
            deviceIdGps
            assetId
            assetName
            status
            disputed
            latitude
            longitude
            unitTime
            deviceData
            unitType
            esn
            linkedDevice {
                id
                esn
                deviceModelId
                deviceModelName
                manufacturerId
                manufacturerName
                simProviderName
                label
            }
        }
        nextToken

    }
}
`;

export const bulkCreateDriverPerformanceOnEventCasesFull = createAsyncThunk(
    "driverPerformance/views/events/redux/bulkCreateDriverPerformanceOnEventCasesFull",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const response = await clientMutation(
                `mutation bulkCreateDriverPerformanceCasesFull ($driverScope: Int!, $limitedBy: Int!, $eventCategories: Int!, $eventsLimit: Int!, $includeHighlight: Boolean!, $driverIds: [Int], $eventIds: [DriverEventsInput]) {
                    data: bulkCreateDriverPerformanceCasesFull (driverScope: $driverScope, limitedBy: $limitedBy, eventCategories: $eventCategories, eventsLimit: $eventsLimit, includeHighlight: $includeHighlight, driverIds: $driverIds, eventIds: $eventIds) {
                        stepFunctionId
                        transactionId
                        transactionType
                        processStartDate
                    }
                }`,
                {
                    driverScope: 0,
                    limitedBy: 0,
                    eventCategories: 0,
                    eventsLimit: 0,
                    includeHighlight: false,
                    driverIds: payload?.driverIds,
                    eventIds: payload?.eventIds
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            if (!response?.data) {
                throw 'bulkCreateDriverPerformanceOnEventCasesFull : No data found';
            }

            return response?.data;
        } catch (exc) {
            ExceptionManager(exc, route, "bulkCreateDriverPerformanceOnEventCasesFull");
            return rejectWithValue(exc);
        }
    }
);

export { driverPerformanceSearchEvents };

export const updateDriverPerformanceMainItem = createAsyncThunk(
    "driverPerformance/views/events/redux/updateDriverPerformanceMainItem",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { key, status } = payload;
            
            // Return the data needed for the reducer
            return { key, status };
        } catch (exc) {
            ExceptionManager(exc, route, "updateDriverPerformanceMainItem");
            return rejectWithValue(exc);
        }
    }
);

//Slice
const driverPerformanceEventsTabRedux = createSlice({
    name: "driverPerformance/eventsTab",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        updateEventAssignDriver(state, action) {
            const { rowId, esn, driverId, driverName } = action.payload;
            const updatedEvents = state.events.map(event => {
                if (event.rowId === rowId && event.esn === esn) {
                    return {
                        ...event,
                        driverId: driverId,
                        driverName: driverName
                    };
                }
                return event;
            });
            return { ...state, events: updatedEvents };
        },
        updateEventAssignStatus(state, action) {
            const { rowId, esn, status } = action.payload;

            // Check if we need to remove the event
            const shouldRemove = status?.entityId === 'falsepositive';

            if (shouldRemove) {
                // Filter out the event with matching rowId and esn
                const updatedEvents = state.events.filter(event =>
                    !(event.rowId === rowId && event.esn === esn)
                );
                return { ...state, events: updatedEvents };
            }

            // Otherwise, update as normal
            const updatedEvents = state.events.map(event => {
                if (event.rowId === rowId && event.esn === esn) {
                    // Update the status field with the new
                    let deviceData = validateJsonObject(validateJsonObject(event.deviceData));
                    deviceData.caseEventStatus = status?.entityId || status;
                    return {
                        ...event,
                        deviceData: JSON.stringify(JSON.stringify(deviceData))
                    };
                }
                return event;
            });
            return { ...state, events: updatedEvents };
        },
        // Add this new reducer after updateEventAssignStatus (around line 186)
        updateEventAssignStatusBulk(state, action) {
            const { events, status } = action.payload;

            // Create a Set for faster lookup of events to update
            const eventsToUpdateSet = new Set(
                events.map(event => `${event.rowId}_${event.esn}`)
            );

            // Update all matching events
            const updatedEvents = state.events.map(event => {
                const eventKey = `${event.rowId}_${event.esn}`;

                if (eventsToUpdateSet.has(eventKey)) {
                    // Update the status field with the new status
                    let deviceData = validateJsonObject(validateJsonObject(event.deviceData));
                    deviceData.caseEventStatus = status?.entityId || status;

                    return {
                        ...event,
                        deviceData: JSON.stringify(JSON.stringify(deviceData))
                    };
                }
                return event;
            });

            return { ...state, events: updatedEvents };
        },
        // Add this new reducer after updateEventAssignStatusBulk
        updateEventsWithCasesBulk(state, action) {
            const { cases } = action.payload;
            if (!cases || !Array.isArray(cases) || cases.length === 0) {
                console.warn('No cases data to update');
                return state;
            }
            let updatedEvents = [...state.events];
            // Process each case
            cases.forEach(caseItem => {
                const { caseNumber, rowIds } = caseItem;
                if (!caseNumber || !rowIds) {
                    console.warn('Invalid case item:', caseItem);
                    return;
                }

                try {
                    // Parse rowIds JSON string
                    const parsedRowIds = validateJsonObject(rowIds);

                    if (!Array.isArray(parsedRowIds)) {
                        console.warn('rowIds is not an array:', parsedRowIds);
                        return;
                    }

                    // Create a Set for faster lookup: "rowId_unitId" -> caseNumber
                    const eventKeysMap = new Set(
                        parsedRowIds.map(item => `${item.rowId}_${item.unitId}`)
                    );

                    // Update events that match this case
                    updatedEvents = updatedEvents.map(event => {
                        const eventKey = `${event.rowId}_${event.esn}`;

                        if (eventKeysMap.has(eventKey)) {
                            // Update deviceData with case number
                            let deviceData = validateJsonObject(validateJsonObject(event.deviceData));
                            deviceData.caseNumber = caseNumber;

                            return {
                                ...event,
                                caseNumber: caseNumber, // Add at root level too
                                deviceData: JSON.stringify(JSON.stringify(deviceData))
                            };
                        }
                        return event;
                    });

                } catch (error) {
                    console.error('Error parsing rowIds for case:', caseNumber, error);
                }
            });

            return { ...state, events: updatedEvents };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //driverPerformanceSearchEvents
        builder.addCase(driverPerformanceSearchEvents.pending, (state, action) => {
            const { conditions } = action.meta.arg
            let pagination = conditions.find((item) => item.page);

            if (pagination?.page == 1) {
                state.events = []
                state.nextToken = null
            }

            state.loading = true;
        });
        builder.addCase(driverPerformanceSearchEvents.fulfilled, (state, action) => {

            const newItems = action.payload?.items || [];
            const nextToken = action.payload?.nextToken || null;

            const itemsWithCountId = newItems.map((item, index) => ({
                ...item,
                countId: state.events.length + index + 1
            }));

            state.events = [...state.events, ...itemsWithCountId]
            state.nextToken = nextToken
            state.loading = false;
        });
        builder.addCase(driverPerformanceSearchEvents.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(bulkCreateDriverPerformanceOnEventCasesFull.pending, (state, action) => {
            state.loadBulkCreateEventCases = true;
            state.bulkResponse = null; // Reset previous response
            state.error = null;
        });
        builder.addCase(bulkCreateDriverPerformanceOnEventCasesFull.fulfilled, (state, action) => {
            state.loadBulkCreateEventCases = false;
            state.bulkResponse = action.payload?.items || [];
        });
        builder.addCase(bulkCreateDriverPerformanceOnEventCasesFull.rejected, (state, action) => {
            state.loadBulkCreateEventCases = false;
            state.error = action.payload;
        });

        // updateDriverPerformanceMainItem
        builder.addCase(updateDriverPerformanceMainItem.fulfilled, (state, action) => {
            const { key, status } = action.payload;
            
            // Validate that the key is within bounds
            if (key >= 0 && key < state.events.length) {
                const statusValue = status?.entityId || status;
                
                // Check if we need to remove the event
                const shouldRemove = statusValue === 'falsepositive';
                
                if (shouldRemove) {
                    // Remove the event from the array
                    state.events.splice(key, 1);
                } else {
                    // Update the event
                    const event = state.events[key];
                    
                    // Parse deviceData (double stringified)
                    let deviceData = validateJsonObject(validateJsonObject(event.deviceData));
                    
                    // Update caseEventStatus with new status
                    deviceData.caseEventStatus = statusValue;
                    
                    // Convert back to original format (double stringified)
                    const updatedDeviceData = JSON.stringify(JSON.stringify(deviceData));
                    
                    // Update the event in the array
                    state.events[key] = {
                        ...event,
                        deviceData: updatedDeviceData
                    };
                }
            }
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = driverPerformanceEventsTabRedux;

// Extract and export each action creator by name
export const { reset, updateEventAssignDriver, updateEventAssignStatus, updateEventAssignStatusBulk, updateEventsWithCasesBulk } = actions;

// Export the reducer, either as a default or named export
export default reducer;
